import { forwardRef, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes, type TextareaHTMLAttributes } from 'react'
import { cn } from '@/lib/utils'
import type { FieldSize, UpboTag } from '@/types'

const sizes: Record<FieldSize, string> = {
  sm: 'h-9 text-[13px]',
  md: 'h-11 text-sm',
  lg: 'h-12 text-base',
}

const base =
  'w-full rounded-squircle bg-surface border border-border text-text placeholder:text-text-faint ' +
  'transition-colors duration-200 focus:outline-none focus:border-text focus:ring-1 focus:ring-border-strong ' +
  'disabled:opacity-60 disabled:bg-surface-2'

function Label({ htmlFor, children, hint }: { htmlFor?: string; children: ReactNode; hint?: string }) {
  return (
    <label htmlFor={htmlFor} className="flex items-center justify-between gap-2 text-[11px] font-medium uppercase tracking-[0.14em] text-text-muted">
      <span>{children}</span>
      {hint && <span className="text-[11px] font-medium text-text-faint">{hint}</span>}
    </label>
  )
}

function ErrorText({ children }: { children: ReactNode }) {
  return <span className="text-xs font-medium text-error">{children}</span>
}

interface InputProps extends InputHTMLAttributes<HTMLInputElement>, UpboTag {
  label?: string
  hint?: string
  error?: string
  icon?: ReactNode
  fieldSize?: FieldSize
}

/** Ô nhập một dòng — kèm nhãn, icon và thông báo lỗi. */
export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { label, hint, error, icon, fieldSize = 'md', className, id, ...props },
  ref,
) {
  const inputId = id ?? props.name
  return (
    <div className="flex flex-col gap-1.5 w-full">
      {label && <Label htmlFor={inputId} hint={hint}>{label}</Label>}
      <div className="relative">
        {icon && (
          <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-text-faint pointer-events-none">{icon}</span>
        )}
        <input
          ref={ref}
          id={inputId}
          data-upbo-type="input"
          className={cn(base, sizes[fieldSize], 'px-3.5', icon && 'pl-10', error && 'border-error focus:border-error focus:ring-error-soft', className)}
          {...props}
        />
      </div>
      {error && <ErrorText>{error}</ErrorText>}
    </div>
  )
})

interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement>, UpboTag {
  label?: string
  hint?: string
  error?: string
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { label, hint, error, className, id, rows = 5, ...props },
  ref,
) {
  const inputId = id ?? props.name
  return (
    <div className="flex flex-col gap-1.5 w-full">
      {label && <Label htmlFor={inputId} hint={hint}>{label}</Label>}
      <textarea
        ref={ref}
        id={inputId}
        rows={rows}
        data-upbo-type="input"
        className={cn(base, 'py-3 px-3.5 text-sm leading-relaxed resize-y min-h-28', error && 'border-error focus:border-error focus:ring-error-soft', className)}
        {...props}
      />
      {error && <ErrorText>{error}</ErrorText>}
    </div>
  )
})

interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement>, UpboTag {
  label?: string
  hint?: string
  error?: string
  fieldSize?: FieldSize
  options: { value: string; label: string }[]
}

/** Ô chọn — dùng thẻ <select> gốc để trên điện thoại hiện bánh xe quen thuộc. */
export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { label, hint, error, options, fieldSize = 'md', className, id, ...props },
  ref,
) {
  const inputId = id ?? props.name
  return (
    <div className="flex flex-col gap-1.5 w-full">
      {label && <Label htmlFor={inputId} hint={hint}>{label}</Label>}
      <div className="relative">
        <select
          ref={ref}
          id={inputId}
          data-upbo-type="input"
          className={cn(base, sizes[fieldSize], 'pl-3.5 pr-9 appearance-none cursor-pointer', error && 'border-error', className)}
          {...props}
        >
          {options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <svg
          className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-text-faint"
          width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
          strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
        >
          <path d="m6 9 6 6 6-6" />
        </svg>
      </div>
      {error && <ErrorText>{error}</ErrorText>}
    </div>
  )
})
